import React, { useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";
import axios from "axios";
import Swal from "sweetalert2";

/* =========================
    CONFIGURACIÓN
========================== */
const API =
  process.env.REACT_APP_SUA_API_INTERNA_URL ||
  "https://suadesarrollo.azurewebsites.net/api";

const COLORES = {
  tinta: "#1f2d3d",
  gris: "#6c757d",
  borde: "#e3e6f0",
  fondo: "#f8f9fc",
  ok: "#1cc88a",
  okFondo: "#e8f8f1",
  error: "#e74a3b",
  errorFondo: "#fdecea",
  aviso: "#f6c23e",
  primario: "#4e73df",
};

/* =========================
    UTILIDADES DE LA RESPUESTA
========================== */
/** Lee la respuesta del API venga en camelCase o PascalCase. */
function normalizarRespuesta(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return {
      estructurada: false,
      texto: typeof data === "string" ? data : JSON.stringify(data, null, 2),
    };
  }

  const errores = data.errores ?? data.Errores ?? [];
  const totales = data.registrosTotales ?? data.RegistrosTotales;
  const cargados = data.registrosCargados ?? data.RegistrosCargados;

  return {
    estructurada:
      totales !== undefined || cargados !== undefined || Array.isArray(errores),
    mensaje:
      data.mensaje ??
      data.Mensaje ??
      data.message ??
      data.Message ??
      data.title ??
      "",
    totales: Number(totales ?? 0),
    cargados: Number(cargados ?? 0),
    errores: Array.isArray(errores) ? errores.map(String) : [],
    texto: JSON.stringify(data, null, 2),
  };
}

/** [2,3,4,7,9,10] -> "2–4, 7, 9–10" (corta si hay demasiados tramos). */
function filasComoRangos(filas, maxTramos = 12) {
  const ordenadas = [...new Set(filas)].sort((a, b) => a - b);
  if (!ordenadas.length) return "";

  const tramos = [];
  let inicio = ordenadas[0];
  let previo = ordenadas[0];

  for (let i = 1; i <= ordenadas.length; i++) {
    const actual = ordenadas[i];
    if (actual === previo + 1) {
      previo = actual;
      continue;
    }
    tramos.push(inicio === previo ? `${inicio}` : `${inicio}–${previo}`);
    inicio = previo = actual;
  }

  return tramos.length > maxTramos
    ? `${tramos.slice(0, maxTramos).join(", ")} y ${tramos.length - maxTramos} tramo(s) más`
    : tramos.join(", ");
}

/** Agrupa "Fila N: mensaje" por mensaje, para no repetir 160 veces lo mismo. */
function agruparErrores(errores) {
  const porMensaje = new Map();
  const sueltos = [];

  errores.forEach((error) => {
    const coincidencia = error.match(/^Fila\s+(\d+)\s*:\s*(.+)$/i);
    if (!coincidencia) {
      sueltos.push(error);
      return;
    }
    const [, fila, mensaje] = coincidencia;
    if (!porMensaje.has(mensaje)) porMensaje.set(mensaje, []);
    porMensaje.get(mensaje).push(Number(fila));
  });

  const grupos = [...porMensaje.entries()]
    .map(([mensaje, filas]) => ({ mensaje, filas, cantidad: filas.length }))
    .sort((a, b) => b.cantidad - a.cantidad);

  sueltos.forEach((mensaje) =>
    grupos.push({ mensaje, filas: [], cantidad: 1 }),
  );
  return grupos;
}

/** Descarga todos los errores en un .txt (uno por línea). */
function descargarErrores(errores, idSolicitud) {
  const blob = new Blob(["\uFEFF" + errores.join("\r\n")], {
    type: "text/plain;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = `errores-cargue-estandares-${idSolicitud ?? "solicitud"}.txt`;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* =========================
    CONTENIDO DEL SWEETALERT (nodos, sin inyectar HTML del servidor)
========================== */
function el(tag, { style, text } = {}, ...hijos) {
  const nodo = document.createElement(tag);
  if (style) nodo.style.cssText = style;
  if (text !== undefined) nodo.textContent = text;
  hijos.filter(Boolean).forEach((h) => nodo.appendChild(h));
  return nodo;
}

function tarjeta(etiqueta, valor, color, fondo) {
  return el(
    "div",
    {
      style: `flex:1 1 140px;padding:12px 14px;border-radius:10px;background:${fondo};border:1px solid ${COLORES.borde};text-align:left;`,
    },
    el("div", {
      style: `font-size:12px;color:${COLORES.gris};font-weight:600;text-transform:uppercase;letter-spacing:.04em;`,
      text: etiqueta,
    }),
    el("div", {
      style: `font-size:26px;font-weight:800;color:${color};line-height:1.2;margin-top:2px;`,
      text: valor.toLocaleString("es-CO"),
    }),
  );
}

function construirContenido(r, status) {
  const raiz = el("div", {
    style: `text-align:left;color:${COLORES.tinta};font-size:14px;`,
  });

  if (status) {
    raiz.appendChild(
      el("div", {
        style: `font-size:12px;color:${COLORES.gris};margin-bottom:8px;`,
        text: `Respuesta del servidor · HTTP ${status}`,
      }),
    );
  }

  // Respuesta sin la estructura esperada: se muestra tal cual.
  if (!r.estructurada) {
    raiz.appendChild(
      el("pre", {
        style: `margin:0;padding:12px;border-radius:8px;background:${COLORES.fondo};white-space:pre-wrap;max-height:320px;overflow:auto;font-size:13px;`,
        text: r.texto || "(respuesta vacía)",
      }),
    );
    return raiz;
  }

  if (r.mensaje) {
    raiz.appendChild(
      el("p", {
        style: "margin:0 0 14px;font-size:15px;font-weight:600;",
        text: r.mensaje,
      }),
    );
  }

  // Tarjetas de resumen
  raiz.appendChild(
    el(
      "div",
      { style: "display:flex;flex-wrap:wrap;gap:10px;margin-bottom:12px;" },
      tarjeta("Registros totales", r.totales, COLORES.tinta, "#fff"),
      tarjeta("Cargados", r.cargados, COLORES.ok, COLORES.okFondo),
      tarjeta(
        "Errores",
        r.errores.length,
        r.errores.length ? COLORES.error : COLORES.gris,
        r.errores.length ? COLORES.errorFondo : "#fff",
      ),
    ),
  );

  // Barra de progreso
  if (r.totales > 0) {
    const porcentaje = Math.round((r.cargados / r.totales) * 100);
    raiz.appendChild(
      el(
        "div",
        { style: "margin-bottom:16px;" },
        el(
          "div",
          {
            style: `height:8px;border-radius:4px;background:${COLORES.errorFondo};overflow:hidden;`,
          },
          el("div", {
            style: `height:100%;width:${porcentaje}%;background:${COLORES.ok};`,
          }),
        ),
        el("div", {
          style: `font-size:12px;color:${COLORES.gris};margin-top:4px;`,
          text: `${porcentaje}% de los registros se cargaron.`,
        }),
      ),
    );
  }

  if (!r.errores.length) return raiz;

  // Errores agrupados por tipo
  const grupos = agruparErrores(r.errores);
  raiz.appendChild(
    el("div", {
      style: "font-weight:700;margin-bottom:8px;",
      text: `Errores encontrados (${grupos.length} tipo${grupos.length === 1 ? "" : "s"})`,
    }),
  );

  const lista = el("div", {
    style:
      "display:flex;flex-direction:column;gap:8px;max-height:220px;overflow:auto;padding-right:4px;",
  });
  grupos.forEach((g) => {
    lista.appendChild(
      el(
        "div",
        {
          style: `display:flex;gap:10px;align-items:flex-start;padding:10px 12px;border-radius:8px;background:${COLORES.errorFondo};border-left:4px solid ${COLORES.error};`,
        },
        el("span", {
          style: `flex:none;min-width:34px;padding:2px 8px;border-radius:999px;background:${COLORES.error};color:#fff;font-size:12px;font-weight:700;text-align:center;`,
          text: String(g.cantidad),
        }),
        el(
          "div",
          { style: "min-width:0;" },
          el("div", {
            style: "font-weight:600;overflow-wrap:anywhere;",
            text: g.mensaje,
          }),
          g.filas.length
            ? el("div", {
                style: `font-size:12px;color:${COLORES.gris};margin-top:2px;`,
                text: `Filas: ${filasComoRangos(g.filas)}`,
              })
            : null,
        ),
      ),
    );
  });
  raiz.appendChild(lista);

  // Detalle completo, plegable
  const detalle = el(
    "details",
    { style: "margin-top:12px;" },
    el("summary", {
      style: `cursor:pointer;font-weight:600;color:${COLORES.primario};`,
      text: `Ver los ${r.errores.length} errores uno por uno`,
    }),
  );
  const ul = el("ol", {
    style: `margin:8px 0 0;padding:10px 10px 10px 34px;max-height:200px;overflow:auto;background:${COLORES.fondo};border-radius:8px;font-size:12.5px;line-height:1.5;`,
  });
  r.errores.forEach((e) => ul.appendChild(el("li", { text: e })));
  detalle.appendChild(ul);
  raiz.appendChild(detalle);

  return raiz;
}

/** Muestra en SweetAlert todo lo que respondió el API. */
function mostrarResultadoCargue(data, status, idSolicitud) {
  const r = normalizarRespuesta(data);
  const falloHttp = status >= 400;

  let icono = "info";
  let titulo = "Respuesta del cargue";

  if (r.estructurada) {
    if (!r.errores.length && !falloHttp) {
      icono = "success";
      titulo = "Cargue exitoso";
    } else if (r.cargados > 0) {
      icono = "warning";
      titulo = "Cargue parcial";
    } else {
      icono = "error";
      titulo = "El archivo no se cargó";
    }
  } else if (falloHttp || !status) {
    icono = "error";
    titulo = "Error en el cargue";
  }

  return Swal.fire({
    icon: icono,
    title: titulo,
    html: construirContenido(r, status),
    width: 760,
    confirmButtonText: "Entendido",
    confirmButtonColor: COLORES.primario,
    showDenyButton: r.errores?.length > 0,
    denyButtonText: "Descargar errores (.txt)",
    denyButtonColor: COLORES.gris,
    preDeny: () => {
      descargarErrores(r.errores, idSolicitud);
      return false; // mantiene abierto el mensaje
    },
  }).then(() => r);
}

/* =========================
    COMPONENTE
========================== */
function CreaEstandares(props) {
  const [isLoading, setLoading] = useState(false);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [archivos, setArchivos] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [sedeId, setSedeId] = useState(0);
  const [avisoSedes, setAvisoSedes] = useState("");

  const inputArchivos = useRef(null);

  const closeModal = () => {
    if (!isLoading) setIsOpen(false);
  };

  /* =========================
      SEDES
  ========================== */
  useEffect(() => {
    if (!modalIsOpen) return undefined;

    const controller = new AbortController();
    setSedeId(0);
    setAvisoSedes("");

    axios
      .get(`${API}/ObtenerSedesPorInstitucionId`, {
        params: { InstitucionId: props.idInstitucion },
        signal: controller.signal,
      })
      .then(({ data }) => {
        const activas = Array.isArray(data)
          ? data.filter((s) => s.aprobada === true)
          : [];
        setSedes(activas);
        if (!activas.length) setAvisoSedes("Solo cuenta con sede principal.");
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.error("Error obteniendo las sedes:", error);
        setSedes([]);
        setAvisoSedes("Solo cuenta con sede principal.");
      });

    return () => controller.abort();
  }, [modalIsOpen, props.idInstitucion]);

  /* =========================
      CARGUE
  ========================== */
  const insertarArchivos = async () => {
    if (!archivos || archivos.length === 0) {
      Swal.fire(
        "Atención",
        "Por favor selecciona al menos un archivo CSV.",
        "warning",
      );
      return;
    }

    const formData = new FormData();
    Array.from(archivos).forEach((archivo) =>
      formData.append("files", archivo),
    );

    setLoading(true);

    try {
      const { data, status } = await axios.post(
        `${API}/CargaMasivosEstandares`,
        formData,
        {
          params: {
            SolicitudId: props.idSolicitud,
            FechaCorte: new Date().getFullYear(),
            SedeId: sedeId,
          },
        },
      );

      console.log(`[CargaMasivosEstandares] HTTP ${status}:`, data);

      const r = normalizarRespuesta(data);
      const huboCarga = !r.estructurada || r.cargados > 0;

      // Si se cargó algo, se cierra el modal; si no, se deja abierto para corregir y reintentar.
      if (huboCarga) setIsOpen(false);

      setLoading(false);
      await mostrarResultadoCargue(data, status, props.idSolicitud);

      if (huboCarga) {
        setArchivos(null);
        setSedeId(0);
        if (props.onCargado) props.onCargado();
        else window.location.reload();
      }
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data ?? error.message;

      console.error(
        `[CargaMasivosEstandares] error HTTP ${status ?? "sin respuesta"}:`,
        data,
      );

      setLoading(false);
      await mostrarResultadoCargue(data, status, props.idSolicitud);
    } finally {
      setLoading(false);
    }
  };

  /* =========================
      RENDER
  ========================== */
  return (
    <div>
      <button
        className="btn btn-primary"
        onClick={() => setIsOpen(true)}
        disabled={isLoading}
      >
        Adjuntar Documento
      </button>

      <ReactModal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Carga masiva de estándares"
      >
        <div className="container">
          <div className="container-fluid">
            <div className="card shadow mb-4">
              <div className="card-header py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  Sube el documento
                </h6>
                <button
                  type="button"
                  className="close"
                  aria-label="Cerrar"
                  onClick={closeModal}
                  disabled={isLoading}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="card-body">
                <div className="form-group">
                  <label htmlFor="sede-estandares">
                    <strong>Selecciona la sede</strong>
                  </label>
                  <select
                    id="sede-estandares"
                    className="form-control"
                    value={sedeId}
                    onChange={(e) => setSedeId(Number(e.target.value))}
                    disabled={isLoading}
                  >
                    {sedes.length === 0 ? (
                      <option value={0}>Principal</option>
                    ) : (
                      <>
                        <option value={0}>-- Selecciona una sede --</option>
                        {sedes.map((sede) => (
                          <option key={sede.id} value={sede.id}>
                            {sede.nombre}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {avisoSedes && (
                    <small className="form-text text-muted">{avisoSedes}</small>
                  )}
                </div>

                <div className="form-group mt-3">
                  <label htmlFor="archivos-estandares">
                    <strong>Selecciona los archivos CSV</strong>
                  </label>
                  <input
                    id="archivos-estandares"
                    ref={inputArchivos}
                    type="file"
                    name="files"
                    accept=".csv"
                    multiple
                    className="form-control"
                    onChange={(e) => setArchivos(e.target.files)}
                    disabled={isLoading}
                  />
                </div>

                {archivos && archivos.length > 0 && (
                  <div className="mt-3">
                    <strong>Archivos seleccionados: {archivos.length}</strong>
                    <ul className="mt-2 mb-0">
                      {Array.from(archivos).map((archivo) => (
                        <li key={`${archivo.name}-${archivo.size}`}>
                          {archivo.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-3">
                  <button
                    type="button"
                    className={`btn ${isLoading ? "btn-secondary" : "btn-primary"} btn-user btn-block`}
                    onClick={insertarArchivos}
                    disabled={isLoading}
                  >
                    {isLoading ? "Cargando…" : "Guardar"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-secondary btn-user btn-block mt-2"
                    onClick={closeModal}
                    disabled={isLoading}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ReactModal>
    </div>
  );
}

export default CreaEstandares;
